<template>
  <UIModal
    v-model="modalIsShowing"
    :title="modalTitle"
    :reset-scroll="true"
    :content-description="modalTitle"
    :close-on-backdrop-press="!loading"
    :close-on-escape-press="!loading"
    @change="$emit('change', $event)"
    @show="modalsHidden = false"
    @shown="type === 'guest-add' ? $refs['guest-form'].focusFirstInput() : null"
    @hidden="onModalHidden"
  >
    <template #backdrop>
      <div class="modal-backdrop h-full w-full opacity-50"></div>
    </template>
    <template #header="{ hideModal }">
      <div
        class="flex items-center pl-22 pr-15 my-15 tablet:pl-25 tablet:mt-20"
        :class="{
          'justify-between': showModalTitle,
          'justify-end': !showModalTitle,
        }"
      >
        <h2
          v-show="showModalTitle"
          :title="modalTitle"
          data-private
          class="font-medium text-xl text-primary-dark leading-tight line-clamp-2"
        >
          {{ modalTitle }}
        </h2>
        <button
          :disabled="loading"
          class="self-start flex-shrink-0 hidden h-40 w-40 flex items-center justify-center -my-5 ml-15 rounded-full
          focus:outline-none tablet:block any-pointer-fine:block"
          :class="{
            'focus:bg-gray-200 hover:bg-gray-200': !loading,
            'opacity-75 cursor-not-allowed': loading,
          }"
          @click="hideModal"
        >
          <FontAwesomeIcon
            :icon="['fal', 'times']"
            class="h-full w-full text-2xl text-gray-600"
          />
        </button>
      </div>
    </template>
    <template #default>
      <GuestForm
        v-if="(type === 'guest-add' || type === 'guest-update') && !modalsHidden"
        ref="guest-form"
        :autofocus-first-input="type === 'guest-add'"
        :show-default-add-photo="!existingGuest.photoStoragePath"
        :show-barcode-value-input="barcodeValueSupport"
        :show-qr-code="type !== 'guest-add' && QrCodeSupport"
        :qr-code-type="event && event.guestIdFieldForCheckIn === 'barcodeValue'
        && event.barcodeType === 'Code39' ? 'Code39' : 'QRCode'"
        :disable-form="loading"
        :first-name-first-order="firstNameFirstOrder"
        :existing-guest-data="existingGuest"
        :guest-photo="type === 'guest-update' ? existingGuest.photoURL : ''"
        :guest-badge="guest.badgeURL"
        :status-options="guestStatusOptions"
        :max-extra-guests="maxExtraGuests"
        :show-max-extra-guests-reached="showMaxExtraGuestsReached"
        class="mb-5"
        @upgrade-event="$emit('upgrade-event')"
        @remove-photo="guest.photoURL = ''"
        @successful-submit="onSuccessfulFormSubmit($event)"
      />
      <div
        v-else-if="type === 'guest-add-limit-reached'"
        class="text-primary flex flex-col items-center px-35 mb-10 tablet:mb-20"
      >
        <div class="relative h-110 w-110 flex items-center justify-center bg-gray-200 rounded-full">
          <FontAwesomeIcon
            :icon="['fad', 'users']"
            class="icon-max-guests h-60"
          />
          <div
            class="absolute bottom-0 right-0 h-42 w-42 flex items-center justify-center text-white bg-primary
            rounded-full shadow-1dp"
          >
            <FontAwesomeIcon
              :icon="['far', 'angle-double-up']"
              class="icon-upgrade text-3xl text-white bg-primary rounded-full"
            />
          </div>
        </div>
        <p class="mt-20">
          You reached the maximum number of guests for this event
          <span class="font-bold">(Max. {{ event.maxGuests ? event.maxGuests : 0 }} guests)</span>
          <span class="inline-block mt-5">Upgrade your event to add more guests.</span>
        </p>
      </div>
      <div
        v-else
        class="text-primary flex flex-col items-center px-35 mb-10 tablet:mb-20"
      >
        <div class="relative">
          <UIAvatar
            :image-src="existingGuest.photoURL"
            :image-alt="existingGuest.fullName"
            :title="existingGuest.fullName"
            class="guest-avatar bg-primary-light opacity-50 shadow-1dp"
          >
            <FontAwesomeIcon
              :icon="['fad', 'user-circle']"
              class="icon-no-image-placeholder fa-swap-opacity h-100"
            />
          </UIAvatar>
          <FontAwesomeIcon
            :icon="['fad', 'exclamation-triangle']"
            class="icon-remove absolute bottom-0 right-0 text-4xl"
          />
        </div>
        <p class="mt-20">
          Do you want to delete
          <span class="font-bold text-lg leading-tight">
            {{ existingGuest.fullName }}
          </span>
          ?
        </p>
      </div>
      <Transition name="fade-medium">
        <div
          v-show="loading"
          class="loading-surface absolute top-0 h-full w-full flex flex-col items-center justify-center rounded-8 z-10"
        >
          <UILoader
            v-show="loading"
            class="text-5xl text-secondary"
          />
          <p class="text-xl text-secondary mt-20">
            {{ type === 'guest-add' ? 'Adding guest' : 'Updating guest' }}
          </p>
        </div>
      </Transition>
    </template>
    <template #footer>
      <div
        class="flex items-center justify-between py-20 px-20 mobile-l:pb-15 mobile-l:pt-12
        touch-device:flex-row-reverse"
      >
        <div class="flex -mx-5 touch-device:flex-row-reverse">
          <div class="px-5">
            <button
              :disabled="loading"
              class="action-button h-45 flex items-center text-white tracking-wide leading-tight py-6 rounded-10
              focus:outline-none"
              :class="{
                'color-accent-primary px-40 bg-secondary': type !== 'guest-delete',
                'color-accent-danger px-35 bg-danger': type === 'guest-delete',
                'focus:opacity-90 hover:opacity-90': !loading,
                'opacity-75 cursor-not-allowed': loading,
              }"
              @click="onActionPress"
            >
              <FontAwesomeIcon
                v-if="type === 'guest-add-limit-reached'"
                :icon="['far', 'angle-double-up']"
                class="icon-upgrade text-2xl mr-15"
              />
              {{ primaryButtonLabel }}
              <span
                v-if="type !== 'guest-add-limit-reached'"
                class="hidden ml-5 mobile-m:inline"
              >
                guest
              </span>
            </button>
          </div>
          <div class="px-5">
            <button
              :disabled="loading"
              class="h-45 font-medium text-gray-600 tracking-wide py-6 px-20 rounded-10 focus:outline-none
              mobile-l:px-25"
              :class="{
                'focus:bg-gray-200 hover:bg-gray-200': !loading,
                'opacity-75 cursor-not-allowed': loading,
              }"
              @click="onCancelPress"
            >
              {{ type === 'guest-add-limit-reached' ? 'Back' : 'Cancel' }}
            </button>
          </div>
        </div>
        <button
          v-if="type === 'guest-update'"
          class="h-45 w-45 flex items-center justify-center font-medium text-danger tracking-wide rounded-full
          focus:outline-none"
          :class="{
            'focus:bg-gray-200 hover:bg-gray-200': !loading,
            'opacity-75 cursor-not-allowed': loading,
          }"
          @click="onDeletePress"
        >
          <FontAwesomeIcon
            :icon="['fas', 'trash']"
            class="text-xl"
          />
        </button>
      </div>
    </template>
  </UIModal>
</template>

<script>
import { FBStorageRef } from '@/helpers/firebaseServices';
import UIModal from '@/components/UI/UIModal';
import UILoader from '@/components/UI/UILoader';
import UIAvatar from '@/components/UI/UIAvatar';
import GuestForm from '@/components/guest/GuestForm';
import { guestStatus } from '@/utils/utilityFunctions';

export default {
  model: {
    prop: 'showing',
    event: 'change',
  },
  props: {
    showing: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      validator(value) {
        return ['guest-add', 'guest-add-limit-reached', 'guest-update', 'guest-delete'].includes(value);
      },
      default: 'guest-add',
    },
    event: {
      type: Object,
      default: null,
    },
    firstNameFirstOrder: {
      type: Boolean,
      default: false,
    },
    badgeSupport: {
      type: Boolean,
      default: false,
    },
    QrCodeSupport: {
      type: Boolean,
      default: false,
    },
    barcodeValueSupport: {
      type: Boolean,
      default: false,
    },
    existingGuest: {
      type: Object,
      default() {
        return {};
      },
    },
    maxAdditionalGuests: {
      type: Number,
      default: 0,
    },
  },
  components: {
    UIModal,
    UILoader,
    UIAvatar,
    GuestForm,
  },
  data: () => ({
    modalIsShowing: false,
    modalsHidden: true, // Hidden completely (Includes transitions)
    unwatchPhotoURL: null,
    unwatchBadgeURL: null,
    cancelBackToModal: false,
    guest: {
      badgeURL: '',
    },
  }),
  watch: {
    showing: {
      immediate: true,
      handler(value) {
        // Sync showing with the modal's showing
        this.modalIsShowing = value;
        this.cancelBackToModal = false;

        if (value) {
          // Handle badge URL
          this.guest.badgeURL = '';
          if (this.type === 'guest-update') {
            // Watch guest badge
            // eslint-disable-next-line func-names
            this.unwatchBadgeURL = this.$watch(function () {
              if (!this.badgeSupport || !this.existingGuest?.badgeStoragePath) return '';
              return this.existingGuest.badgeStoragePath;
              // eslint-disable-next-line func-names
            }, function (badgeStoragePath) {
              if (!badgeStoragePath) {
                this.guest.badgeURL = '';
                return;
              }

              FBStorageRef.child(badgeStoragePath)
                .getDownloadURL()
                .then((badgeURL) => {
                  this.guest.badgeURL = badgeURL;
                })
                .catch((error) => {
                  this.guest.badgeURL = '';
                  console.error(error);
                });
            }, { immediate: true });

            return;
          }
        }

        // Modal is hiding
        if (this.unwatchBadgeURL !== null) this.unwatchBadgeURL();
      },
    },
  },
  computed: {
    modalTitle() {
      if (this.type === 'guest-add') return 'New guest';
      if (this.type === 'guest-update') return `Editing ${this.existingGuest?.fullName || 'guest'}`;
      return `Deleting ${this.existingGuest?.fullName || 'guest'}`;
    },
    primaryButtonLabel() {
      if (this.type === 'guest-add') return 'Add';
      if (this.type === 'guest-add-limit-reached') return 'Upgrade';
      if (this.type === 'guest-update') return 'Save';
      return 'Delete';
    },
    guestStatusOptions() {
      return Object.entries(guestStatus)
        // Filter statuses that should be visible
        .filter((status) => (status[1].isVisible && status[1]?.isVisible(this.event)) || !status[1].isVisible)
        .sort((a, b) => a[1].order - b[1].order)
        .map((status) => ({ value: parseInt(status[0], 10), label: status[1].name }));
    },
    showModalTitle() {
      return this.type !== 'guest-delete' && this.type !== 'guest-add-limit-reached';
    },
    maxExtraGuests() {
      if (this.type === 'guest-add') {
        return Math.min(this.maxAdditionalGuests - 1, this.event?.maxExtraGuestsPerGuest || 10);
      }
      // Guest update
      return Math.min(
        (this.existingGuest?.totalExtraGuests ?? 0) + this.maxAdditionalGuests,
        this.event?.maxExtraGuestsPerGuest ?? 10,
      );
    },
    showMaxExtraGuestsReached() {
      if (this.maxExtraGuests < (this.event?.maxExtraGuestsPerGuest || 10)) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onModalHidden() {
      this.modalsHidden = true;
      this.$emit('hidden');
    },
    onActionPress() {
      if (this.type === 'guest-add-limit-reached') {
        this.$emit('upgrade-event');
      } else if (this.type === 'guest-delete') {
        this.$emit('delete-guest', {
          guestId: this.existingGuest.id,
          photoStoragePath: this.existingGuest?.photoStoragePath,
        });
      } else {
        this.$refs['guest-form'].submitForm();
      }
    },
    onCancelPress() {
      if (this.cancelBackToModal) {
        this.$emit('guest-update', this.existingGuest.id);
        this.cancelBackToModal = false;
        document.activeElement.blur();
      } else {
        this.$emit('change', false);
      }
    },
    onDeletePress() {
      this.cancelBackToModal = true;
      this.$emit('guest-delete', this.existingGuest.id);
    },
    onSuccessfulFormSubmit(eventData) {
      if (this.type === 'guest-update') {
        this.$emit('update-guest', {
          oldGuestData: this.existingGuest,
          guestData: eventData.guest,
          photo: eventData.photo,
        });
      } else {
        this.$emit('add-guest', {
          guest: eventData.guest,
          photoFile: eventData.photo.file,
        });
      }
    },
  },
  beforeDestroy() {
    if (this.unwatchBadgeURL !== null) this.unwatchBadgeURL();
  },
};
</script>

<style scoped>
.modal-backdrop {
  background: radial-gradient(theme('colors.primary.default'), theme('colors.primary.dark') 5%);
}

.loading-surface {
  background-color: hsla(0, 0%, 100%, 0.8);
}

.color-accent-primary {
  --button-color-accent: 40, 50%, 54%;
}

.color-accent-danger {
  --button-color-accent: 0, 90%, 58%;
}

.guest-avatar {
  --avatar-size: theme('spacing.110');
}

.icon-max-guests {
  @apply w-auto !important;

  --fa-primary-color: theme('colors.primary.light');
  --fa-secondary-color: theme('colors.gray.500');
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 1;
}

.icon-upgrade {
  @apply w-auto !important;
}

.icon-no-image-placeholder {
  @apply w-auto !important;

  --fa-primary-color: theme('colors.gray.200');
  --fa-secondary-color: theme('colors.primary.light');
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 1;
}

.action-button {
  @apply transition-all duration-75;

  text-shadow: 0 -1px 1px hsl(var(--button-color-accent)),
  1px 0 1px hsl(var(--button-color-accent)),
  0 1px 1px hsl(var(--button-color-accent)),
  -1px 0 1px hsl(var(--button-color-accent));
  box-shadow: 0 2px 0 hsl(var(--button-color-accent));
}

.icon-remove {
  --fa-primary-color: theme('colors.white');
  --fa-primary-opacity: 1;
  --fa-secondary-color: theme('colors.danger');
  --fa-secondary-opacity: 1;
}
</style>
